import type { FC } from 'react';
import { IMAGE_PREFIX_TO_REPLACE, getSourcePropsByWidth } from './response-image-utils';

interface Props {
    id: string;
    src: string;
    title?: string;
    alt?: string;
    width?: string | number;
    height?: string | number;
    maxWidth?: string | number;
    loading?: 'eager' | 'lazy';
    fetchpriority?: 'high' | 'low' | 'auto';
    className?: string;
    borderRadius?: string;
    borderWidth?: string;
    borderColor?: string;
}

/**
 * Component to build images using the <picture> tag for different image and screen sizes.
 *
 * To follow best Lighthouse practices:
 *
 *  1. Always that is possible, pass the width/height props for the <img> tag have
 *      explicit width/height and for this component build the different sources based on the image size.
 *
 *  2. If you cannot pass width/height, pass a max-width if possible, with max-width
 *      this component can also build the different sources based on the image size. Sometimes the width/height
 *      will be dynamic, but you know what the image will not be bigger than a specific size.
 *
 * 3. If you cannot pass width/height and max-width, this component will build the different sources
 *    based on screen sizes instead of the image size.
 */
export const ResponsiveImage: FC<Props> = ({
    id,
    src,
    title,
    alt,
    width,
    height,
    maxWidth,
    loading,
    fetchpriority,
    className,
    borderRadius,
    borderWidth,
    borderColor,
}) => {
    if (!src.includes(IMAGE_PREFIX_TO_REPLACE)) {
        return (
            <img
                data-testid={id}
                id={id}
                src={src}
                title={title}
                alt={alt}
                width={width}
                height={height}
                loading={loading}
                className={className}
                style={{ borderRadius, borderWidth, borderColor }}
            />
        );
    }

    const widthForProps =
        typeof width === 'number' || (width && !width.includes('%')) ? width : undefined;
    const maxWidthForProps =
        typeof maxWidth === 'number' || (maxWidth && !maxWidth.includes('%'))
            ? maxWidth
            : undefined;

    const { defaultSrc, sourcesProp } = getSourcePropsByWidth(
        src,
        widthForProps ?? maxWidthForProps,
    );

    return sourcesProp ? (
        <picture data-testid={`${id}-picture`} className={className}>
            <source
                key={`${id}-source`}
                data-testid={`${id}-source`}
                srcSet={sourcesProp.src}
                media={sourcesProp.media}
            />

            <img
                data-testid={id}
                id={id}
                src={defaultSrc}
                title={title}
                alt={alt}
                width={width}
                height={height}
                loading={loading}
                fetchpriority={fetchpriority}
                className={className}
                style={{ borderRadius, borderWidth, borderColor }}
            />
        </picture>
    ) : (
        <img
            data-testid={id}
            id={id}
            src={defaultSrc}
            title={title}
            alt={alt}
            width={width}
            height={height}
            loading={loading}
            fetchpriority={fetchpriority}
            className={className}
            style={{ borderRadius, borderWidth, borderColor }}
        />
    );
};

ResponsiveImage.defaultProps = {
    title: undefined,
    alt: undefined,
    width: undefined,
    height: undefined,
    maxWidth: undefined,
    loading: undefined,
    fetchpriority: undefined,
    className: undefined,
    borderRadius: undefined,
    borderWidth: undefined,
    borderColor: undefined,
};
